import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Eyebrow, scrollToId, ease, openCalendly } from "./utils";
import GrowthSignal from "./GrowthSignal";

export default function Hero({ ready = true }) {
  const reduce = useReducedMotion();
  const reveal = (delay) => ({
    initial: { opacity: 0, y: 22 },
    animate: ready ? { opacity: 1, y: 0 } : { opacity: 0, y: 22 },
    transition: { duration: 0.8, ease, delay: ready ? delay : 0 },
  });

  return (
    <section data-testid="hero-section" className="relative overflow-hidden bg-brand-gray-light pt-28 pb-20 md:pt-32 md:pb-24">
      {/* layered background depth */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1.2, ease }}
        className="pointer-events-none absolute inset-0"
      >
        <div className="hero-grid absolute inset-0" />
        <div className="grain-overlay absolute inset-0 opacity-[0.035]" />
        <div className="absolute inset-0 bg-[radial-gradient(58%_55%_at_72%_32%,rgba(200,154,61,0.12),transparent_70%)]" />
        <div className="absolute -top-32 right-0 h-[520px] w-[520px] rounded-full bg-brand-gold/10 blur-[120px]" />
        <div className="absolute top-1/3 -left-24 h-[400px] w-[400px] rounded-full bg-brand-gold/5 blur-[120px]" />
        {/* thin gold contour lines flowing toward the right visual */}
        <svg className="absolute right-0 top-0 hidden h-full w-2/3 lg:block" viewBox="0 0 800 700" fill="none" preserveAspectRatio="xMaxYMid slice">
          {[0, 1, 2].map((i) => (
            <motion.path
              key={i}
              d={`M-40 ${360 + i * 70} C 220 ${300 + i * 60}, 380 ${420 - i * 40}, 620 ${250 - i * 55} S 820 ${120 - i * 40}, 880 ${60 - i * 30}`}
              stroke="#C89A3D"
              strokeOpacity={0.16 - i * 0.03}
              strokeWidth="1"
              initial={{ pathLength: 0 }}
              animate={ready ? { pathLength: 1 } : { pathLength: 0 }}
              transition={{ duration: 2, ease, delay: ready ? 0.3 + i * 0.15 : 0 }}
            />
          ))}
        </svg>
      </motion.div>

      <div className="relative max-w-[1400px] mx-auto px-6 md:px-12 grid lg:grid-cols-[1.15fr_0.85fr] gap-14 lg:gap-12 items-center">
        {/* Left — copy */}
        <div>
          <motion.div {...reveal(0.45)}>
            <Eyebrow>Revenue Growth Systems for Service Businesses</Eyebrow>
          </motion.div>

          <motion.h1
            {...reveal(0.55)}
            className="mt-7 font-serif text-[2.7rem] leading-[1.04] sm:text-5xl lg:text-[4rem] tracking-tight text-brand-black"
          >
            Turn Missed Opportunities Into <span className="gold-text">Predictable Revenue.</span>
          </motion.h1>

          <motion.p {...reveal(0.68)} className="mt-7 max-w-xl font-sans text-base md:text-lg leading-relaxed text-brand-black/70">
            Apex Flow helps service businesses identify where revenue is slipping through the cracks—then installs the
            follow-up, organization, campaigns, and visibility needed to create more predictable growth.
          </motion.p>

          <motion.div {...reveal(0.8)} className="mt-9 flex flex-col sm:flex-row gap-4">
            <button
              data-testid="hero-book-call"
              onClick={openCalendly}
              className="inline-flex items-center justify-center gap-2 rounded-full bg-brand-black text-white px-7 py-4 font-sans text-sm font-semibold tracking-wide transition-all duration-300 shadow-luxe hover:-translate-y-0.5 hover:shadow-[0_28px_70px_-18px_rgba(200,154,61,0.45)]"
            >
              Book a Free Revenue Opportunity Call
              <ArrowRight className="h-4 w-4" />
            </button>
            <button
              data-testid="hero-see-how"
              onClick={() => scrollToId("how-it-works")}
              className="inline-flex items-center justify-center gap-2 rounded-full border border-brand-black/15 bg-white/60 backdrop-blur text-brand-black px-7 py-4 font-sans text-sm font-semibold tracking-wide transition-all duration-300 hover:border-brand-gold hover:text-brand-gold-deep"
            >
              See How It Works
            </button>
          </motion.div>

          <motion.p {...reveal(0.92)} className="mt-7 max-w-md font-sans text-sm leading-relaxed text-brand-black/55">
            No pressure. No one-size-fits-all plan. Start with a clear look at what is happening inside your business.
          </motion.p>
        </div>

        {/* Right — Growth Signal 3D visual */}
        <div className="order-last">
          <GrowthSignal ready={ready} />
        </div>
      </div>
    </section>
  );
}
